import { api } from './api'

/** Cents to "$12.50". Mirrors the server's formatter. */
export function formatCents(cents: number): string {
  const sign = cents < 0 ? '-' : ''
  const abs = Math.abs(cents)
  return `${sign}$${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`
}

export interface PaymentPackage {
  id: 'starter' | 'regular' | 'large'
  label: string
  priceCents: number
  creditCents: number
}

export interface PaymentsConfig {
  enabled: boolean
  /** False when cash play is not permitted where the player is. */
  moneyAllowed: boolean
  moneyBlockedReason: string | null
  moneyBlockedMessage: string | null
  location: { country: string | null; region: string | null }
  /** Local threshold, which is not 18 everywhere. */
  minimumAge: number
  packages: PaymentPackage[]
  cashout: {
    minimumCents: number
    feeRate: number
    stepCents: number
    settlementWindow: string
  }
  /** Prize coins per dollar. Fixed, so conversion is exact both ways. */
  coinsPerDollar: number
}

export interface CashoutRow {
  id: string
  grossCents: number
  feeCents: number
  netCents: number
  paypalEmail: string
  status: 'requested' | 'paid' | 'rejected' | 'cancelled'
  adminNote: string | null
  createdAt: string
  settledAt: string | null
}

export interface PurchaseRow {
  id: string
  amountCents: number
  creditedCents: number
  status: 'pending' | 'paid' | 'failed' | 'refunded'
  createdAt: string
  paidAt: string | null
}

export interface CashBalance {
  cashCents: number
  pendingCashoutCents: number
  availableCents: number
  practiceCoins: number
  prizeCoins: number
  cashouts: CashoutRow[]
  purchases: PurchaseRow[]
}

export interface ConvertResult {
  ok: true
  cashCents: number
  prizeCoins: number
  movedCents: number
  movedCoins: number
}

/** Cash into prize coins, ready to stake. */
export function buyPrizeCoins(cents: number) {
  return api<ConvertResult>('/payments/coins/buy', {
    method: 'POST',
    body: { cents },
    auth: true,
  })
}

/** Prize coins back into withdrawable cash. */
export function redeemPrizeCoins(coins: number) {
  return api<ConvertResult>('/payments/coins/redeem', {
    method: 'POST',
    body: { coins },
    auth: true,
  })
}

export interface Activity {
  online: number
  queued: number
  rooms: number
}

/** Live lobby activity. Public, and cheap enough to poll. */
export function getActivity(): Promise<Activity> {
  return api<Activity>('/stats')
}

export function getPaymentsConfig(): Promise<PaymentsConfig> {
  return api<PaymentsConfig>('/payments/config')
}

export function getCashBalance(): Promise<CashBalance> {
  return api<CashBalance>('/payments/balance', { auth: true })
}

/**
 * Starts a Stripe Checkout session and returns the URL to send the player to.
 *
 * Nothing is credited here. The account is only topped up when Stripe's signed
 * webhook confirms the payment, so returning from Checkout does not by itself
 * mean the money has landed.
 */
export async function startCheckout(packageId: PaymentPackage['id']): Promise<string> {
  const result = await api<{ url: string }>('/payments/checkout', {
    method: 'POST',
    body: { packageId },
    auth: true,
  })
  return result.url
}

export function requestCashout(amountDollars: number, paypalEmail: string) {
  return api<{ id: string; grossCents: number; feeCents: number; netCents: number; message: string }>(
    '/payments/cashout',
    { method: 'POST', body: { amountDollars, paypalEmail }, auth: true },
  )
}

export function cancelCashout(id: string) {
  return api<{ ok: true }>(`/payments/cashout/${id}/cancel`, { method: 'POST', auth: true })
}

/** Client-side preview of the split. The server recomputes it authoritatively. */
export function quoteCashout(amountDollars: number, feeRate: number) {
  const grossCents = Math.max(0, Math.floor(amountDollars)) * 100
  const feeCents = Math.ceil(grossCents * feeRate)
  return { grossCents, feeCents, netCents: grossCents - feeCents }
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export interface AdminSummary {
  players: number
  chargedCents: number
  purchaseCount: number
  paidOutCents: number
  feesCollectedCents: number
  pendingCashoutCents: number
  pendingCashoutCount: number
  playerBalanceCents: number
  playerCashCents: number
  playerPrizeCoinCents: number
}

export interface AdminUser {
  id: string
  email: string
  displayName: string
  createdAt: string
  lastSeenAt: string
  strikes: number
  isAdmin: boolean
  coins: number
  prizeCoins: number
  cashCents: number
  pendingCashoutCents: number
  chargedCents: number
  earnedCents: number
  withdrawnCents: number
}

export interface AdminCashout extends CashoutRow {
  userId: string
  email: string
  displayName: string
  cashCents: number | null
}

export function checkAdmin() {
  return api<{ isAdmin: boolean }>('/admin/me', { auth: true })
}

export function getAdminSummary() {
  return api<AdminSummary>('/admin/summary', { auth: true })
}

export function getAdminUsers(q?: string) {
  const query = q ? `?q=${encodeURIComponent(q)}` : ''
  return api<{ users: AdminUser[] }>(`/admin/users${query}`, { auth: true })
}

export function getAdminCashouts(status?: string) {
  const query = status ? `?status=${encodeURIComponent(status)}` : ''
  return api<{ cashouts: AdminCashout[] }>(`/admin/cashouts${query}`, { auth: true })
}

export function markCashoutPaid(id: string, note?: string) {
  return api<{ ok: true }>(`/admin/cashouts/${id}/paid`, {
    method: 'POST',
    body: { note },
    auth: true,
  })
}

export function rejectCashout(id: string, note?: string) {
  return api<{ ok: true }>(`/admin/cashouts/${id}/reject`, {
    method: 'POST',
    body: { note },
    auth: true,
  })
}

export interface AgeStatus {
  verified: boolean
  requiredAge: number
  verifiedAt: string | null
}

export function getAgeStatus(): Promise<AgeStatus> {
  return api<AgeStatus>('/payments/age', { auth: true })
}

export function submitDateOfBirth(dateOfBirth: string) {
  return api<{ ok: true; age: number; requiredAge: number }>('/payments/age', {
    method: 'POST',
    body: { dateOfBirth },
    auth: true,
  })
}
